import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Shield,
  Smartphone,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  Eye,
  Trash2,
  Activity,
  Users,
  Clock,
  MapPin,
} from 'lucide-react';
import { securityApi, Session, AuditLog, SecurityStats } from '../../api/security';
import { Pagination } from '../../components/shared';
import { toast } from 'sonner';

interface SecurityPageProps {
  isDark: boolean;
}

const ITEMS_PER_PAGE = 20;

const eventTypeColors: Record<string, string> = {
  LOGIN_SUCCESS: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  LOGIN_FAILED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  LOGOUT: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  SESSION_CREATED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  SESSION_REVOKED: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  DEVICE_LIMIT_EXCEEDED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  GEO_ANOMALY: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  ACCOUNT_SHARING_SUSPECTED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  RAPID_LOCATION_CHANGE: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
};

const severityColors: Record<string, string> = {
  INFO: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  WARNING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  CRITICAL: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

export default function SecurityPage({ isDark }: SecurityPageProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Sessions state
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsSearch, setSessionsSearch] = useState('');
  const [sessionsActiveFilter, setSessionsActiveFilter] = useState<string>('all');
  const [sessionsPlatformFilter, setSessionsPlatformFilter] = useState<string>('all');
  
  // Audit logs state
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsSearch, setLogsSearch] = useState('');
  const [logsSeverityFilter, setLogsSeverityFilter] = useState<string>('all');
  const [logsEventTypeFilter, setLogsEventTypeFilter] = useState<string>('all');
  
  // Dialogs
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessionToRevoke, setSessionToRevoke] = useState<Session | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await securityApi.getStats();
      setStats(data);
    } catch (error) {
      
      toast.error('Не удалось загрузить статистику');
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await securityApi.getSessions({
        page: sessionsPage,
        limit: ITEMS_PER_PAGE,
        search: sessionsSearch || undefined,
        isActive: sessionsActiveFilter !== 'all' ? sessionsActiveFilter : undefined,
        platform: sessionsPlatformFilter !== 'all' ? sessionsPlatformFilter : undefined,
      });
      setSessions(response.data);
      setSessionsTotal(response.total);
    } catch (error) {
      
      toast.error('Не удалось загрузить сессии');
    } finally {
      setIsLoading(false);
    }
  }, [sessionsPage, sessionsSearch, sessionsActiveFilter, sessionsPlatformFilter]);

  const loadAuditLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await securityApi.getAuditLogs({
        page: logsPage,
        limit: ITEMS_PER_PAGE,
        search: logsSearch || undefined,
        severity: logsSeverityFilter !== 'all' ? logsSeverityFilter : undefined,
        eventType: logsEventTypeFilter !== 'all' ? logsEventTypeFilter : undefined,
      });
      setAuditLogs(response.data);
      setLogsTotal(response.total);
    } catch (error) {
      
      toast.error('Не удалось загрузить аудит-лог');
    } finally {
      setIsLoading(false);
    }
  }, [logsPage, logsSearch, logsSeverityFilter, logsEventTypeFilter]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'sessions') {
      loadSessions();
    }
  }, [activeTab, loadSessions]);

  useEffect(() => {
    if (activeTab === 'audit-logs') {
      loadAuditLogs();
    }
  }, [activeTab, loadAuditLogs]);

  const handleRevokeSession = async () => {
    if (!sessionToRevoke) return;
    
    try {
      await securityApi.revokeSession(sessionToRevoke.id);
      toast.success('Сессия отозвана');
      setSessionToRevoke(null);
      loadSessions();
      loadStats();
    } catch (error) {
      console.error('Failed to revoke session:', error);
      toast.error('Не удалось отозвать сессию');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    return `${diffDays} д назад`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>Безопасность</h1>
          <p className={cn(
            'text-sm mt-1',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Мониторинг сессий, аудит-логов и событий безопасности</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            loadStats();
            if (activeTab === 'sessions') loadSessions();
            if (activeTab === 'audit-logs') loadAuditLogs();
          }}
          className={cn(isDark && 'border-slate-700 text-slate-300 hover:bg-slate-800')}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Обновить
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={cn(
          'grid w-full grid-cols-3',
          isDark && 'bg-slate-800'
        )}>
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          <TabsTrigger value="sessions">Сессии</TabsTrigger>
          <TabsTrigger value="audit-logs">Аудит-лог</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/30">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Активные сессии
                  </p>
                  <p className={cn('text-2xl font-bold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.activeSessions || 0}
                  </p>
                </div>
              </div>
            </Card>

            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Активные пользователи
                  </p>
                  <p className={cn('text-2xl font-bold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.uniqueActiveUsers || 0}
                  </p>
                </div>
              </div>
            </Card>

            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Критические (24ч)
                  </p>
                  <p className={cn('text-2xl font-bold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.criticalEvents24h || 0}
                  </p>
                </div>
              </div>
            </Card>

            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-yellow-100 dark:bg-yellow-900/30">
                  <Shield className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Подозрительные (7д)
                  </p>
                  <p className={cn('text-2xl font-bold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.suspiciousActivity7d || 0}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Login Stats */}
            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}>
              <h3 className={cn(
                'text-lg font-semibold mb-4',
                isDark ? 'text-white' : 'text-slate-900'
              )}>Активность входов (24ч)</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Успешные входы</span>
                  </div>
                  <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.successfulLogins24h || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Неудачные входы</span>
                  </div>
                  <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.failedLogins24h || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-orange-500" />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Отозвано сессий</span>
                  </div>
                  <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.revokedSessions24h || 0}
                  </span>
                </div>
              </div>
            </Card>

            {/* Top Countries */}
            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}>
              <h3 className={cn(
                'text-lg font-semibold mb-4',
                isDark ? 'text-white' : 'text-slate-900'
              )}>Топ стран</h3>
              <div className="space-y-3">
                {stats?.topCountries?.slice(0, 5).map((item, index) => (
                  <div key={item.country || index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                        {item.country || 'Неизв.'}
                      </span>
                    </div>
                    <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      {item.count}
                    </span>
                  </div>
                ))}
                {(!stats?.topCountries || stats.topCountries.length === 0) && (
                  <p className={cn('text-sm', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    Нет данных
                  </p>
                )}
              </div>
            </Card>

            {/* By Platform */}
            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1A1A22]' : 'bg-white'
            )}>
              <h3 className={cn(
                'text-lg font-semibold mb-4',
                isDark ? 'text-white' : 'text-slate-900'
              )}>По платформам</h3>
              <div className="space-y-3">
                {stats?.topPlatforms?.slice(0, 5).map((item, index) => (
                  <div key={item.platform || index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-slate-400" />
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                        {item.platform ? item.platform.toUpperCase() : 'Неизв.'}
                      </span>
                    </div>
                    <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      {item.count}
                    </span>
                  </div>
                ))}
                {(!stats?.topPlatforms || stats.topPlatforms.length === 0) && (
                  <p className={cn('text-sm', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    Нет данных
                  </p>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions">
          <Card className={cn(
            'rounded-2xl border-0',
            isDark ? 'bg-[#1A1A22]' : 'bg-white'
          )}>
            {/* Filters */}
            <div className={cn(
              'p-5 border-b flex flex-col lg:flex-row gap-4',
              isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Поиск по устройству, IP, юзеру..."
                  value={sessionsSearch}
                  onChange={(e) => {
                    setSessionsSearch(e.target.value);
                    setSessionsPage(1);
                  }}
                  className={cn(
                    'pl-10',
                    isDark && 'bg-slate-800 border-slate-700 text-white'
                  )}
                />
              </div>
              <Select value={sessionsActiveFilter} onValueChange={(v) => { setSessionsActiveFilter(v); setSessionsPage(1); }}>
                <SelectTrigger className={cn('w-[150px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="true">Активные</SelectItem>
                  <SelectItem value="false">Неактивные</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sessionsPlatformFilter} onValueChange={(v) => { setSessionsPlatformFilter(v); setSessionsPage(1); }}>
                <SelectTrigger className={cn('w-[150px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Платформа" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все платформы</SelectItem>
                  <SelectItem value="ios">iOS</SelectItem>
                  <SelectItem value="android">Android</SelectItem>
                  <SelectItem value="web">Web</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-slate-700' : ''}>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Пользователь</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Устройство</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Локация</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Статус</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Активность</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : sessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className={cn(
                        'text-center py-8',
                        isDark ? 'text-slate-400' : 'text-slate-500'
                      )}>
                        Сессии не найдены
                      </TableCell>
                    </TableRow>
                  ) : sessions.map((session) => (
                    <TableRow key={session.id} className={isDark ? 'border-slate-700' : ''}>
                      <TableCell>
                        <div>
                          <p className={cn('font-medium', isDark ? 'text-white' : 'text-slate-900')}>
                            {session.user?.username || 'Неизв.'}
                          </p>
                          <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                            {session.user?.email || (session.user?.isAnonymous ? 'Аноним' : '-')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                              {session.deviceName || session.deviceModel || 'Неизв. устройство'}
                            </p>
                            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                              {session.platform} {session.osVersion}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                              {session.country || 'Неизв.'}
                            </p>
                            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                              {session.ipAddress || '-'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={session.isActive 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }>
                          {session.isActive ? 'Активна' : 'Неактивна'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            {formatRelativeTime(session.lastActiveAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Подробнее о сессии"
                            onClick={() => setSelectedSession(session)}
                            className={isDark ? 'hover:bg-slate-700' : ''}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {session.isActive && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Отозвать сессию"
                              onClick={() => setSessionToRevoke(session)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {sessions.length > 0 && (
              <div className={cn(
                'p-4 border-t',
                isDark ? 'border-slate-700' : 'border-slate-200'
              )}>
                <Pagination
                  isDark={isDark}
                  currentPage={sessionsPage}
                  totalPages={Math.ceil(sessionsTotal / ITEMS_PER_PAGE)}
                  total={sessionsTotal}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setSessionsPage}
                />
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit-logs">
          <Card className={cn(
            'rounded-2xl border-0',
            isDark ? 'bg-[#1A1A22]' : 'bg-white'
          )}>
            {/* Filters */}
            <div className={cn(
              'p-5 border-b flex flex-col lg:flex-row gap-4',
              isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Поиск по IP, устройству..."
                  value={logsSearch}
                  onChange={(e) => {
                    setLogsSearch(e.target.value);
                    setLogsPage(1);
                  }}
                  className={cn(
                    'pl-10',
                    isDark && 'bg-slate-800 border-slate-700 text-white'
                  )}
                />
              </div>
              <Select value={logsSeverityFilter} onValueChange={(v) => { setLogsSeverityFilter(v); setLogsPage(1); }}>
                <SelectTrigger className={cn('w-[150px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Важность" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все уровни</SelectItem>
                  <SelectItem value="INFO">Инфо</SelectItem>
                  <SelectItem value="WARNING">Предупреждение</SelectItem>
                  <SelectItem value="CRITICAL">Критично</SelectItem>
                </SelectContent>
              </Select>
              <Select value={logsEventTypeFilter} onValueChange={(v) => { setLogsEventTypeFilter(v); setLogsPage(1); }}>
                <SelectTrigger className={cn('w-[180px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Тип события" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все события</SelectItem>
                  <SelectItem value="LOGIN_SUCCESS">Успешный вход</SelectItem>
                  <SelectItem value="LOGIN_FAILED">Неудачный вход</SelectItem>
                  <SelectItem value="SESSION_REVOKED">Сессия отозвана</SelectItem>
                  <SelectItem value="GEO_ANOMALY">Гео-аномалия</SelectItem>
                  <SelectItem value="ACCOUNT_SHARING_SUSPECTED">Шеринг аккаунта</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-slate-700' : ''}>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Время</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Событие</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Важность</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Пользователь</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>IP / Локация</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Детали</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className={cn(
                        'text-center py-8',
                        isDark ? 'text-slate-400' : 'text-slate-500'
                      )}>
                        Записи аудита не найдены
                      </TableCell>
                    </TableRow>
                  ) : auditLogs.map((log) => (
                    <TableRow key={log.id} className={isDark ? 'border-slate-700' : ''}>
                      <TableCell>
                        <div>
                          <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                            {formatRelativeTime(log.createdAt)}
                          </p>
                          <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                            {formatDate(log.createdAt)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={eventTypeColors[log.eventType] || 'bg-gray-100 text-gray-800'}>
                          {log.eventType.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={severityColors[log.severity]}>
                          {log.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                          {log.user?.username || log.userId?.slice(0, 8) || '-'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                            {log.ipAddress || '-'}
                          </p>
                          <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                            {log.country || '-'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Показать детали"
                            onClick={() => {
                              toast.info(
                                <pre className="text-xs max-w-md overflow-auto">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              );
                            }}
                            className={isDark ? 'hover:bg-slate-700' : ''}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        ) : (
                          <span className={cn('text-sm', isDark ? 'text-slate-500' : 'text-slate-400')}>-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {auditLogs.length > 0 && (
              <div className={cn(
                'p-4 border-t',
                isDark ? 'border-slate-700' : 'border-slate-200'
              )}>
                <Pagination
                  isDark={isDark}
                  currentPage={logsPage}
                  totalPages={Math.ceil(logsTotal / ITEMS_PER_PAGE)}
                  total={logsTotal}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setLogsPage}
                />
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Session Details Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent className={cn(
          'max-w-lg',
          isDark && 'bg-[#1A1A22] border-slate-700 text-white'
        )}>
          <DialogHeader>
            <DialogTitle>Детали сессии</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              Полная информация о сессии
            </DialogDescription>
          </DialogHeader>
          {selectedSession && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Пользователь</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.user?.username || 'Неизв.'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Email</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.user?.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Устройство</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.deviceName || selectedSession.deviceModel || 'Неизв.'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Платформа</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.platform} {selectedSession.osVersion}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>IP-адрес</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.ipAddress || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Локация</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.city ? `${selectedSession.city}, ` : ''}{selectedSession.country || 'Неизв.'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Версия прил.</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.appVersion || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Статус</p>
                  <Badge className={selectedSession.isActive 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                  }>
                    {selectedSession.isActive ? 'Активна' : 'Неактивна'}
                  </Badge>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Создана</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {formatDate(selectedSession.createdAt)}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Активность</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {formatDate(selectedSession.lastActiveAt)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Session Confirmation */}
      <AlertDialog open={!!sessionToRevoke} onOpenChange={() => setSessionToRevoke(null)}>
        <AlertDialogContent className={isDark ? 'bg-[#1A1A22] border-slate-700' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? 'text-white' : ''}>Отозвать сессию</AlertDialogTitle>
            <AlertDialogDescription className={isDark ? 'text-slate-400' : ''}>
              Отозвать эту сессию? Пользователь будет разлогинен на этом устройстве.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeSession}
              className="bg-red-600 hover:bg-red-700"
            >
              Отозвать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
